import { Shield, FileText, Users, Lock, AlertTriangle, Scale, Mail } from "lucide-react"
import { Link } from "react-router-dom"

export default function TermsPage() {
    return (
        <div className="min-h-screen dark:text-slate-50 custom-scrollbar bg-gradient-to-br from-background via-background to-muted/20 dark:to-muted/10">
            {/* Header */}
            <header className="sticky top-0 z-50 w-full border-b border-accent-500/30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="container flex h-16 items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="relative flex items-center space-x-2">
                            <div className="flex items-center justify-center w-14 h-14 p-0 rounded-full overflow-hidden">
                                <img src={"/logo/green-logo.png"} alt="Sapians-AI Logo" className="w-full h-full object-contain" />
                            </div>

                            <Link
                                to="/"
                                className="text-lg font-semibold leading-tight transition-transform hover:scale-105 hover:text-accent-500"
                            >
                                Sapians-AI
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main content */}
            <main className="container mx-auto px-4 py-12 max-w-4xl">
                {/* Hero section */}
                <div className="text-center mb-12">
                    <div className="w-16 h-16 bg-gradient-to-br from-accent-500 to-accent-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <FileText className="h-8 w-8 text-white" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-balance mb-4 bg-gradient-to-r from-foreground to-accent-500 bg-clip-text text-transparent">
                        Terms and Conditions
                    </h1>
                    <p className="text-lg text-muted-foreground mb-2">Sapians AI Interview Preparation Platform</p>
                    <p className="text-sm text-muted-foreground">
                        Effective Date: January 1, 2025 | Last Updated: January 1, 2025
                    </p>
                </div>

                {/* Quick navigation */}
                <div className="bg-card/50 backdrop-blur-sm rounded-xl p-6 mb-8 border border-accent-500/80 shadow-sm">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <div className="w-6 h-6 bg-accent-500/20 rounded-full flex items-center justify-center">
                            <Shield className="h-4 w-4 text-accent-500" />
                        </div>
                        Quick Navigation
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <a
                            href="#acceptance"
                            className="text-accent-500 hover:text-accent-600 hover:bg-accent-500/10 px-2 py-1 rounded transition-colors"
                        >
                            1. Acceptance of Terms
                        </a>
                        <a
                            href="#services"
                            className="text-accent-500 hover:text-accent-600 hover:bg-accent-500/10 px-2 py-1 rounded transition-colors"
                        >
                            2. Description of Services
                        </a>
                        <a
                            href="#accounts"
                            className="text-accent-500 hover:text-accent-600 hover:bg-accent-500/10 px-2 py-1 rounded transition-colors"
                        >
                            3. User Accounts
                        </a>
                        <a
                            href="#conduct"
                            className="text-accent-500 hover:text-accent-600 hover:bg-accent-500/10 px-2 py-1 rounded transition-colors"
                        >
                            4. User Conduct
                        </a>
                        <a
                            href="#ai-services"
                            className="text-accent-500 hover:text-accent-600 hover:bg-accent-500/10 px-2 py-1 rounded transition-colors"
                        >
                            5. AI Services
                        </a>
                        <a
                            href="#privacy"
                            className="text-accent-500 hover:text-accent-600 hover:bg-accent-500/10 px-2 py-1 rounded transition-colors"
                        >
                            6. Privacy and Data
                        </a>
                        <a
                            href="#payment"
                            className="text-accent-500 hover:text-accent-600 hover:bg-accent-500/10 px-2 py-1 rounded transition-colors"
                        >
                            7. Payment Terms
                        </a>
                        <a
                            href="#liability"
                            className="text-accent-500 hover:text-accent-600 hover:bg-accent-500/10 px-2 py-1 rounded transition-colors"
                        >
                            8. Limitation of Liability
                        </a>
                    </div>
                </div>

                {/* Terms content */}
                <div className="prose prose-gray dark:prose-invert max-w-none">
                    {/* Section 1 */}
                    <section id="acceptance" className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-400 rounded-full flex items-center justify-center shadow-sm">
                                <span className="text-white font-semibold text-sm">1</span>
                            </div>
                            <h2 className="text-xl font-semibold m-0 text-foreground">Acceptance of Terms</h2>
                        </div>
                        <div className="bg-card/60 backdrop-blur-sm rounded-xl p-6 border border-accent-500/80 shadow-sm hover:shadow-md transition-shadow">
                            <p className="text-sm leading-relaxed mb-4">
                                By accessing or using Sapians AI's interview preparation platform ("Service"), you agree to be bound by
                                these Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use our Service.
                            </p>
                            <p className="text-sm leading-relaxed">
                                These Terms constitute a legally binding agreement between you and Sapians AI. We may update these Terms
                                from time to time, and your continued use of the Service constitutes acceptance of any changes.
                            </p>
                        </div>
                    </section>

                    {/* Section 2 */}
                    <section id="services" className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-400 rounded-full flex items-center justify-center shadow-sm">
                                <span className="text-white font-semibold text-sm">2</span>
                            </div>
                            <h2 className="text-xl font-semibold m-0 text-foreground">Description of Services</h2>
                        </div>
                        <div className="bg-card/60 backdrop-blur-sm rounded-xl p-6 border border-accent-500/80 shadow-sm hover:shadow-md transition-shadow">
                            <p className="text-sm leading-relaxed mb-4">
                                Sapians AI provides an AI-powered interview preparation platform that offers:
                            </p>
                            <ul className="text-sm space-y-2 mb-4 ml-4">
                                <li>• Simulated interview sessions with AI-driven feedback</li>
                                <li>• Real-time performance analysis and improvement suggestions</li>
                                <li>• Personalized interview preparation materials</li>
                                <li>• Progress tracking and analytics</li>
                                <li>• Industry-specific interview scenarios</li>
                            </ul>
                            <p className="text-sm leading-relaxed">
                                Our Service is designed to help users improve their interview skills through artificial intelligence
                                technology. Results may vary based on individual effort and engagement.
                            </p>
                        </div>
                    </section>

                    {/* Section 3 */}
                    <section id="accounts" className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-400 rounded-full flex items-center justify-center shadow-sm">
                                <Users className="h-4 w-4 text-white" />
                            </div>
                            <h2 className="text-xl font-semibold m-0 text-foreground">User Accounts and Registration</h2>
                        </div>
                        <div className="bg-card/60 backdrop-blur-sm rounded-xl p-6 border border-accent-500/80 shadow-sm hover:shadow-md transition-shadow">
                            <p className="text-sm leading-relaxed mb-4">
                                To access certain features of our Service, you must create an account. You agree to:
                            </p>
                            <ul className="text-sm space-y-2 mb-4 ml-4">
                                <li>• Provide accurate, current, and complete information</li>
                                <li>• Maintain and update your account information</li>
                                <li>• Keep your login credentials secure and confidential</li>
                                <li>• Notify us immediately of any unauthorized access</li>
                                <li>• Be responsible for all activities under your account</li>
                            </ul>
                            <p className="text-sm leading-relaxed">
                                You must be at least 16 years old to create an account. If you are under 18, you must have parental
                                consent to use our Service.
                            </p>
                        </div>
                    </section>

                    {/* Section 4 */}
                    <section id="conduct" className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-400 rounded-full flex items-center justify-center shadow-sm">
                                <AlertTriangle className="h-4 w-4 text-white" />
                            </div>
                            <h2 className="text-xl font-semibold m-0 text-foreground">User Conduct and Prohibited Activities</h2>
                        </div>
                        <div className="bg-card/60 backdrop-blur-sm rounded-xl p-6 border border-accent-500/80 shadow-sm hover:shadow-md transition-shadow">
                            <p className="text-sm leading-relaxed mb-4">You agree not to use our Service to:</p>
                            <ul className="text-sm space-y-2 mb-4 ml-4">
                                <li>• Violate any applicable laws or regulations</li>
                                <li>• Infringe on intellectual property rights</li>
                                <li>• Upload malicious code or attempt to hack our systems</li>
                                <li>• Share inappropriate, offensive, or harmful content</li>
                                <li>• Impersonate others or provide false information</li>
                                <li>• Interfere with other users' experience</li>
                                <li>• Attempt to reverse engineer our AI technology</li>
                            </ul>
                            <p className="text-sm leading-relaxed">
                                Violation of these terms may result in account suspension or termination at our discretion.
                            </p>
                        </div>
                    </section>

                    {/* Section 5 */}
                    <section id="ai-services" className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-400 rounded-full flex items-center justify-center shadow-sm">
                                <span className="text-white font-semibold text-sm">AI</span>
                            </div>
                            <h2 className="text-xl font-semibold m-0 text-foreground">AI Services and Limitations</h2>
                        </div>
                        <div className="bg-card/60 backdrop-blur-sm rounded-xl p-6 border border-accent-500/80 shadow-sm hover:shadow-md transition-shadow">
                            <div className="bg-accent-500/10 border border-accent-500/30 rounded-lg p-4 mb-4 backdrop-blur-sm">
                                <p className="text-sm font-medium text-accent-500 mb-2">Important Notice:</p>
                                <p className="text-xs leading-relaxed text-muted-foreground">
                                    Our AI technology provides feedback and suggestions based on algorithms and training data. This should
                                    not be considered as professional career advice or guarantee of interview success.
                                </p>
                            </div>
                            <p className="text-sm leading-relaxed mb-4">By using our AI-powered features, you understand that:</p>
                            <ul className="text-sm space-y-2 ml-4">
                                <li>• AI feedback is generated automatically and may not always be accurate</li>
                                <li>• Results are for educational and practice purposes only</li>
                                <li>• We continuously improve our AI but cannot guarantee perfection</li>
                                <li>• Human judgment should always be applied to AI recommendations</li>
                            </ul>
                        </div>
                    </section>

                    {/* Section 6 */}
                    <section id="privacy" className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-400 rounded-full flex items-center justify-center shadow-sm">
                                <Lock className="h-4 w-4 text-white" />
                            </div>
                            <h2 className="text-xl font-semibold m-0 text-foreground">Privacy and Data Protection</h2>
                        </div>
                        <div className="bg-card/60 backdrop-blur-sm rounded-xl p-6 border border-accent-500/80 shadow-sm hover:shadow-md transition-shadow">
                            <p className="text-sm leading-relaxed mb-4">
                                Your privacy is important to us. Our collection and use of personal information is governed by our
                                Privacy Policy, which is incorporated into these Terms by reference.
                            </p>
                            <p className="text-sm leading-relaxed mb-4">
                                By using our Service, you consent to the collection, processing, and storage of your data as described
                                in our Privacy Policy, including:
                            </p>
                            <ul className="text-sm space-y-2 ml-4">
                                <li>• Interview session recordings and transcripts</li>
                                <li>• Performance analytics and progress data</li>
                                <li>• Account information and usage patterns</li>
                                <li>• Communication preferences and feedback</li>
                            </ul>
                        </div>
                    </section>

                    {/* Section 7 */}
                    <section id="payment" className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-400 rounded-full flex items-center justify-center shadow-sm">
                                <span className="text-white font-semibold text-sm">$</span>
                            </div>
                            <h2 className="text-xl font-semibold m-0 text-foreground">Payment Terms and Billing</h2>
                        </div>
                        <div className="bg-card/60 backdrop-blur-sm rounded-xl p-6 border border-accent-500/80 shadow-sm hover:shadow-md transition-shadow">
                            <p className="text-sm leading-relaxed mb-4">
                                Some features of our Service may require payment. By purchasing a subscription or premium features:
                            </p>
                            <ul className="text-sm space-y-2 mb-4 ml-4">
                                <li>• You agree to pay all applicable fees and charges</li>
                                <li>• Payments are processed securely through third-party providers</li>
                                <li>• Subscriptions automatically renew unless cancelled</li>
                                <li>• Refunds are subject to our refund policy</li>
                                <li>• We may change pricing with 30 days notice</li>
                            </ul>
                            <p className="text-sm leading-relaxed">
                                All fees are non-refundable except as required by law or as specified in our refund policy.
                            </p>
                        </div>
                    </section>

                    {/* Section 8 */}
                    <section id="liability" className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-400 rounded-full flex items-center justify-center shadow-sm">
                                <Scale className="h-4 w-4 text-white" />
                            </div>
                            <h2 className="text-xl font-semibold m-0 text-foreground">Limitation of Liability</h2>
                        </div>
                        <div className="bg-card/60 backdrop-blur-sm rounded-xl p-6 border border-accent-500/80 shadow-sm hover:shadow-md transition-shadow">
                            <div className="bg-destructive/10 border border-red-400/30 rounded-lg p-4 mb-4 backdrop-blur-sm">
                                <p className="text-sm font-medium mb-2 text-red-500">Legal Disclaimer:</p>
                                <p className="text-xs leading-relaxed text-muted-foreground">
                                    To the maximum extent permitted by law, Sapians AI shall not be liable for any indirect, incidental,
                                    special, consequential, or punitive damages.
                                </p>
                            </div>
                            <p className="text-sm leading-relaxed mb-4">
                                Our total liability to you for any claims arising from your use of the Service shall not exceed the
                                amount you paid us in the 12 months preceding the claim.
                            </p>
                            <p className="text-sm leading-relaxed">
                                We provide our Service "as is" without warranties of any kind, either express or implied, including but
                                not limited to merchantability, fitness for a particular purpose, or non-infringement.
                            </p>
                        </div>
                    </section>
                </div>

                {/* Contact and acceptance section */}
                <div className="mt-12 space-y-6">
                    <div className="bg-card/60 backdrop-blur-sm rounded-xl p-6 border border-accent-500/80 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-6 h-6 bg-accent-500/20 rounded-full flex items-center justify-center">
                                <Mail className="h-4 w-4 text-accent-500" />
                            </div>
                            <h3 className="text-lg font-semibold text-foreground">Questions or Concerns?</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-4">
                            If you have any questions about these Terms and Conditions, please contact us:
                        </p>
                        <div className="space-y-2 text-sm">
                            <p>Email: legal@sapiensai.com</p>
                            <p>Address: [Company Address]</p>
                            <p>Phone: [Company Phone]</p>
                        </div>
                    </div>

                    <div className="text-center">
                        <a
                            href="/signup?termsAccepted=true"
                            className="inline-flex items-center  gap-2 bg-gradient-to-r from-accent-500 to-accent-400 text-white px-4 py-2 text-sm rounded-lg font-medium hover:from-accent-600 hover:to-accent-500 hover:shadow-lg hover:scale-105 transition-all duration-200 shadow-md"
                        >
                            <Shield className="h-4 w-4" />I Accept These Terms
                        </a>
                        <p className="text-xs text-muted-foreground mt-4 max-w-md mx-auto">
                            By clicking "I Accept These Terms", you acknowledge that you have read, understood, and agree to be bound
                            by these Terms and Conditions.
                        </p>
                    </div>
                </div>
            </main>
        </div>
    )
}
